import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

type PortalRow = {
  label: string
  rightmove: string
  zoopla: string
  onthemarket: string
}

const OVERVIEW: PortalRow[] = [
  {
    label: "Monthly unique visitors",
    rightmove: "~152 million",
    zoopla: "~60 million",
    onthemarket: "~28 million",
  },
  {
    label: "Listed properties (approx.)",
    rightmove: "~1.1 million",
    zoopla: "~750,000",
    onthemarket: "~500,000",
  },
  {
    label: "Member agents",
    rightmove: "~19,000+",
    zoopla: "~14,000+",
    onthemarket: "~13,000+",
  },
  {
    label: "Founded",
    rightmove: "2000",
    zoopla: "2008",
    onthemarket: "2015",
  },
  {
    label: "Ownership",
    rightmove: "Public (FTSE 100)",
    zoopla: "Private (Silver Lake)",
    onthemarket: "CoStar Group",
  },
]

const AGENT_FEATURES: PortalRow[] = [
  {
    label: "Basic listing fee (monthly)",
    rightmove: "From ~£1,000+",
    zoopla: "From ~£400+",
    onthemarket: "From ~£300+",
  },
  {
    label: "Featured listings",
    rightmove: "Premium, Top, Featured",
    zoopla: "Featured, Spotlight",
    onthemarket: "Featured, Premium",
  },
  {
    label: "Lead generation tools",
    rightmove: "Rightmove Discover, Instant Valuation",
    zoopla: "SmartValuations, Lead Reports",
    onthemarket: "Lead Nurture, MyMarket",
  },
  {
    label: "Valuation tool",
    rightmove: "Rightmove Price Comparison",
    zoopla: "Zoopla Estimates (public)",
    onthemarket: "MyMarket intelligence",
  },
  {
    label: "Market data / analytics",
    rightmove: "Rightmove Data, Best Price Guide",
    zoopla: "Market Reports, Zoopla Insights",
    onthemarket: "Agent Performance Reports",
  },
  {
    label: "Video support",
    rightmove: "Embedded video via URL",
    zoopla: "Embedded video via URL",
    onthemarket: "Embedded video via URL",
  },
  {
    label: "Virtual tour support",
    rightmove: "Matterport, EyeSpy360, etc.",
    zoopla: "Matterport, 360 tours",
    onthemarket: "Matterport, 360 tours",
  },
  {
    label: "Floorplan hosting",
    rightmove: "Yes (interactive optional)",
    zoopla: "Yes",
    onthemarket: "Yes",
  },
  {
    label: "CRM integration",
    rightmove: "Rightmove Plus, Feedworks",
    zoopla: "Alto, Jupix, various CRMs",
    onthemarket: "Most CRM feeds supported",
  },
  {
    label: "Branding on listings",
    rightmove: "Logo, banner, office details",
    zoopla: "Logo, office branding, micro-site",
    onthemarket: "Logo, office micro-site",
  },
]

const BUYER_FEATURES: PortalRow[] = [
  {
    label: "Search filters",
    rightmove: "Comprehensive (40+ filters)",
    zoopla: "Strong (35+ filters)",
    onthemarket: "Good (30+ filters)",
  },
  {
    label: "Map-based search",
    rightmove: "Draw-a-search, commute time",
    zoopla: "Area guides, commute search",
    onthemarket: "Map search, commute time",
  },
  {
    label: "Saved search alerts",
    rightmove: "Email + push notifications",
    zoopla: "Email + push + SMS",
    onthemarket: "Email + push notifications",
  },
  {
    label: "Price history",
    rightmove: "Sold prices (Land Registry)",
    zoopla: "Zoopla Estimates + sold prices",
    onthemarket: "Sold prices (limited)",
  },
  {
    label: "School data",
    rightmove: "Nearby schools with ratings",
    zoopla: "School checker with Ofsted",
    onthemarket: "Nearby schools listed",
  },
  {
    label: "Transport links",
    rightmove: "Commute time calculator",
    zoopla: "Transport overlay, commute tool",
    onthemarket: "Station distances",
  },
  {
    label: "Mobile app rating",
    rightmove: "4.8★ (App Store)",
    zoopla: "4.7★ (App Store)",
    onthemarket: "4.5★ (App Store)",
  },
  {
    label: "New-build section",
    rightmove: "Dedicated new-homes portal",
    zoopla: "New-homes section",
    onthemarket: "New-homes section",
  },
]

const PORTAL_STATS = [
  {
    value: "152M",
    label: "Rightmove monthly visits",
    detail:
      "The UK's most-visited property portal by a significant margin, reaching over 80% of property seekers.",
  },
  {
    value: "60M",
    label: "Zoopla monthly visits",
    detail:
      "The UK's second-largest portal with strong brand awareness and data-driven valuation tools.",
  },
  {
    value: "28M",
    label: "OnTheMarket monthly visits",
    detail:
      "The fastest-growing UK portal, now backed by CoStar Group's substantial investment.",
  },
  {
    value: "86%",
    label: "of agents list on all three",
    detail:
      "Most UK agents advertise on all three portals to maximise exposure. The debate is about where to invest the most.",
  },
]

type FaqItem = { q: string; a: string }

const FAQ: FaqItem[] = [
  {
    q: "Which portal gets the most views for my listing?",
    a: "Rightmove consistently delivers the highest volume of views and enquiries, handling around 80% of UK property searches. However, Zoopla and OnTheMarket provide meaningful incremental reach. Most agents find that listing on all three portals maximises exposure, though the majority of leads still come through Rightmove.",
  },
  {
    q: "Is OnTheMarket worth the investment?",
    a: "Since CoStar Group acquired OnTheMarket in 2023, investment in the platform has increased significantly. Traffic is growing, and the lower pricing makes it an attractive option for agents watching their marketing budgets. It is particularly strong for agents who want to reduce their dependence on Rightmove.",
  },
  {
    q: "Can I advertise on just one portal?",
    a: "You can, but it is generally not recommended. While Rightmove alone would cover the majority of searchers, listing on Zoopla and OnTheMarket captures an additional audience that may not use Rightmove. For vendors paying your fees, maximum exposure is expected.",
  },
  {
    q: "How do portal fees compare?",
    a: "Rightmove is typically the most expensive, with monthly fees starting from around £1,000 for a single branch. Zoopla is moderately priced, starting from around £400. OnTheMarket is usually the most affordable, starting from around £300. Exact fees depend on your location, branch count, and which add-on products you choose.",
  },
  {
    q: "Do portals support video listings?",
    a: "All three portals allow you to embed video within your listings via a URL. This is where QuiteRight fits in: you can create professional property videos in 60 seconds and add them to your Rightmove, Zoopla, and OnTheMarket listings to increase engagement and enquiries.",
  },
  {
    q: "Which portal has the best data and analytics?",
    a: "Rightmove offers the most comprehensive agent analytics through Rightmove Plus, including competitive benchmarking and listing performance data. Zoopla provides strong market intelligence through Zoopla Insights and their public property estimates. OnTheMarket's analytics are improving rapidly following the CoStar acquisition.",
  },
  {
    q: "Are Zoopla's property estimates accurate?",
    a: "Zoopla Estimates use an automated valuation model based on sold prices, property characteristics, and local market trends. They provide a useful ballpark figure, but should not replace a professional market appraisal. Accuracy varies by property type and location, with more standard properties in active markets being more reliably estimated.",
  },
  {
    q: "What is the best strategy for portal listings?",
    a: "List on all three portals for maximum reach. Invest in premium/featured listings on Rightmove for your key properties, as it drives the highest traffic. Use Zoopla's valuation tools to attract vendors. Use OnTheMarket's lower cost to maintain presence without overspending. Add video to every listing across all portals to stand out.",
  },
]

/* ------------------------------------------------------------------ */
/*  Components                                                         */
/* ------------------------------------------------------------------ */

function PortalBadge({
  name,
  colour,
}: {
  name: string
  colour: string
}) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold text-white"
      style={{ backgroundColor: colour }}
    >
      {name}
    </span>
  )
}

function PortalComparisonTable({ rows, title }: { rows: PortalRow[]; title: string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[700px] text-left">
        <thead>
          <tr className="border-b-2 border-stone">
            <th className="pb-3 pr-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
              {title}
            </th>
            <th className="pb-3 px-4 text-center text-xs font-medium uppercase tracking-[0.15em]">
              <PortalBadge name="Rightmove" colour="#00DEB6" />
            </th>
            <th className="pb-3 px-4 text-center text-xs font-medium uppercase tracking-[0.15em]">
              <PortalBadge name="Zoopla" colour="#7B2FBE" />
            </th>
            <th className="pb-3 pl-4 text-center text-xs font-medium uppercase tracking-[0.15em]">
              <PortalBadge name="OnTheMarket" colour="#262637" />
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className={`border-b border-stone/50 ${i % 2 === 0 ? "bg-cream/30" : ""}`}
            >
              <td className="py-3.5 pr-4 text-sm font-medium text-charcoal">
                {row.label}
              </td>
              <td className="py-3.5 px-4 text-center text-sm text-slate">
                {row.rightmove}
              </td>
              <td className="py-3.5 px-4 text-center text-sm text-slate">
                {row.zoopla}
              </td>
              <td className="py-3.5 pl-4 text-center text-sm text-slate">
                {row.onthemarket}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function FaqAccordion({ items }: { items: FaqItem[] }) {
  return (
    <dl className="divide-y divide-stone">
      {items.map((item, i) => (
        <details key={i} className="group">
          <summary className="flex cursor-pointer items-start justify-between gap-4 py-5 text-left [&::-webkit-details-marker]:hidden">
            <dt className="text-sm font-semibold leading-relaxed text-charcoal">
              {item.q}
            </dt>
            <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-stone text-slate transition-transform duration-200 group-open:rotate-45">
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                className="size-3"
              >
                <path d="M8 3v10M3 8h10" />
              </svg>
            </span>
          </summary>
          <dd className="pb-5 text-sm leading-relaxed text-slate">{item.a}</dd>
        </details>
      ))}
    </dl>
  )
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function ZooplaVsRightmoveVsOnTheMarket({
  meta,
}: {
  meta: SeoMeta
}) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline:
              "Zoopla vs Rightmove vs OnTheMarket: The Complete UK Portal Comparison for Estate Agents",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-03-12",
            dateModified: "2026-03-12",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>

        <BreadcrumbSchema
          canonical={meta.canonical}
          items={[
            { name: "Guides", path: "/guides" },
            { name: "Zoopla vs Rightmove vs OnTheMarket" },
          ]}
        />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Portal comparison 2026
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Zoopla vs Rightmove vs OnTheMarket
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            The three major UK property portals each have different strengths,
            pricing, and audiences. This guide breaks down everything estate
            agents need to know to make the right investment decisions for
            their listings.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <PortalBadge name="Rightmove" colour="#00DEB6" />
            <PortalBadge name="Zoopla" colour="#7B2FBE" />
            <PortalBadge name="OnTheMarket" colour="#262637" />
          </div>
        </div>
      </section>

      <SocialShareBar />

      {/* Stats */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              By the numbers
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              The UK portal landscape
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PORTAL_STATS.map((stat, i) => (
              <ScrollReveal key={i}>
                <div className="rounded-xl border border-stone bg-white p-6 text-center">
                  <p className="font-display text-4xl font-bold text-charcoal">
                    {stat.value}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-red">
                    {stat.label}
                  </p>
                  <p className="mt-3 text-xs leading-relaxed text-slate">
                    {stat.detail}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Portal deep dives */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              In depth
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Each portal at a glance
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {/* Rightmove */}
            <ScrollReveal>
              <div className="flex h-full flex-col rounded-xl border border-stone bg-white p-6">
                <div className="mb-4">
                  <PortalBadge name="Rightmove" colour="#00DEB6" />
                </div>
                <h3 className="font-display text-lg font-bold text-charcoal">
                  The market leader
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate">
                  <a href="https://www.rightmove.co.uk" target="_blank" rel="noopener noreferrer">Rightmove</a> dominates UK property search with roughly 80% of all
                  property seekers using the platform. Listed on the{" "}
                  <a href="https://www.londonstockexchange.com/indices/ftse-100" target="_blank" rel="noopener noreferrer">FTSE 100</a>,
                  it has the largest audience, the most comprehensive data, and
                  the strongest brand recognition. Most agents consider it
                  non-negotiable.
                </p>
                <div className="mt-4 space-y-2 text-sm">
                  <p className="font-medium text-charcoal">Strengths:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Highest traffic and enquiry volume in the UK
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Best-in-class agent analytics via Rightmove Plus
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Comprehensive sold-price data from{" "}
                      <a href="https://www.gov.uk/government/organisations/land-registry" target="_blank" rel="noopener noreferrer">Land Registry</a>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Draw-a-search and commute time calculators
                    </li>
                  </ul>
                  <p className="mt-3 font-medium text-charcoal">Considerations:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Most expensive portal for agents
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Annual fee increases are common
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      High competition for buyer attention
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>

            {/* Zoopla */}
            <ScrollReveal>
              <div className="flex h-full flex-col rounded-xl border border-stone bg-white p-6">
                <div className="mb-4">
                  <PortalBadge name="Zoopla" colour="#7B2FBE" />
                </div>
                <h3 className="font-display text-lg font-bold text-charcoal">
                  The data innovator
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate">
                  <a href="https://www.zoopla.co.uk" target="_blank" rel="noopener noreferrer">Zoopla</a> pioneered automated property valuations in the UK and
                  continues to innovate with data-driven tools. Now owned by
                  Silver Lake, it is the second-largest portal and offers strong
                  brand awareness, particularly among homeowners curious about
                  their property&rsquo;s value.
                </p>
                <div className="mt-4 space-y-2 text-sm">
                  <p className="font-medium text-charcoal">Strengths:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Zoopla Estimates drive vendor engagement
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      SmartValuations tool generates vendor leads
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Strong CRM integrations (Alto, Jupix)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Competitive pricing compared to Rightmove
                    </li>
                  </ul>
                  <p className="mt-3 font-medium text-charcoal">Considerations:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Lower traffic volume than Rightmove
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Property estimates can frustrate vendors
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Fewer premium listing options
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>

            {/* OnTheMarket */}
            <ScrollReveal>
              <div className="flex h-full flex-col rounded-xl border border-stone bg-white p-6">
                <div className="mb-4">
                  <PortalBadge name="OnTheMarket" colour="#262637" />
                </div>
                <h3 className="font-display text-lg font-bold text-charcoal">
                  The challenger
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate">
                  Originally founded by agents for agents,{" "}
                  <a href="https://www.onthemarket.com" target="_blank" rel="noopener noreferrer">OnTheMarket</a> was
                  acquired by US property data giant{" "}
                  <a href="https://www.costargroup.com" target="_blank" rel="noopener noreferrer">CoStar Group</a> in 2023.
                  This brought significant investment and ambition to close the
                  gap with Rightmove and Zoopla. Traffic is growing, fees are
                  competitive, and the platform is evolving rapidly.
                </p>
                <div className="mt-4 space-y-2 text-sm">
                  <p className="font-medium text-charcoal">Strengths:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Most affordable of the three portals
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      CoStar investment driving rapid platform improvements
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Agent-friendly ethos (founded by agents)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Growing audience, particularly in regional markets
                    </li>
                  </ul>
                  <p className="mt-3 font-medium text-charcoal">Considerations:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Smallest audience of the three
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Fewer data and analytics tools (for now)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Platform still catching up on features
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Overview comparison table */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Side by side
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Platform overview
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <PortalComparisonTable rows={OVERVIEW} title="Overview" />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Agent features table */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              For agents
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Agent tools and features
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base text-slate">
              How each portal supports estate agents with listing management,
              lead generation, data insights, and marketing tools.
            </p>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <PortalComparisonTable rows={AGENT_FEATURES} title="Agent feature" />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Buyer features table */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              For buyers and tenants
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Search and discovery features
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base text-slate">
              The features that property seekers use to find and evaluate
              listings. This matters for agents because it determines the
              quality and intent of the leads each portal generates.
            </p>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <PortalComparisonTable rows={BUYER_FEATURES} title="Buyer feature" />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Pricing deep dive */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Cost comparison
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              What do the portals cost?
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base text-slate">
              Portal fees vary by location, branch count, and which products
              you choose. Here is a general guide to help you budget.
            </p>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 text-center">
                <PortalBadge name="Rightmove" colour="#00DEB6" />
                <p className="mt-4 font-display text-3xl font-bold text-charcoal">
                  £1,000+
                </p>
                <p className="mt-1 text-sm text-slate">per branch, per month</p>
                <ul className="mt-4 space-y-2 text-left text-sm text-slate">
                  <li className="flex items-start gap-2">
                    <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                    Featured listings from £100+ each
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                    Premium listings from £75+ each
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                    Rightmove Discover add-on available
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                    Annual contracts, annual price rises
                  </li>
                </ul>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 text-center">
                <PortalBadge name="Zoopla" colour="#7B2FBE" />
                <p className="mt-4 font-display text-3xl font-bold text-charcoal">
                  £400+
                </p>
                <p className="mt-1 text-sm text-slate">per branch, per month</p>
                <ul className="mt-4 space-y-2 text-left text-sm text-slate">
                  <li className="flex items-start gap-2">
                    <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                    Featured listings available
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                    SmartValuations lead gen included in some tiers
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                    Flexible packages by branch size
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                    Multi-branch discounts available
                  </li>
                </ul>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 text-center">
                <PortalBadge name="OnTheMarket" colour="#262637" />
                <p className="mt-4 font-display text-3xl font-bold text-charcoal">
                  £300+
                </p>
                <p className="mt-1 text-sm text-slate">per branch, per month</p>
                <ul className="mt-4 space-y-2 text-left text-sm text-slate">
                  <li className="flex items-start gap-2">
                    <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                    Most affordable entry point
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                    Featured listings at competitive rates
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                    CoStar investing in new agent tools
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                    Flexible contract terms
                  </li>
                </ul>
              </div>
            </ScrollReveal>
          </div>

          <ScrollReveal>
            <div className="mt-8 rounded-xl border border-stone bg-cream/50 p-6 text-center">
              <p className="text-sm leading-relaxed text-slate">
                Prices are approximate and vary by location and negotiation. Contact
                each portal directly for a tailored quote. All prices exclude VAT.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Which portal is right for you */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Recommendations
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Which portal strategy is right for you?
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 space-y-6">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Single-branch independent agent
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  List on all three portals for maximum reach. Rightmove is your
                  primary lead driver. Use Zoopla&rsquo;s valuation tools to
                  attract vendor instructions. OnTheMarket provides affordable
                  incremental reach. Budget approximately £1,700+ per month for
                  all three.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Multi-branch corporate agency
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  Negotiate multi-branch discounts with all three portals.
                  Invest in Rightmove Featured and Premium listings for
                  high-value properties. Use Zoopla Insights for market
                  intelligence across your branches. Leverage OnTheMarket for
                  regional areas where it performs strongly.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Letting agent
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  All three portals support lettings. Rightmove has the highest
                  volume of tenant searches. Zoopla&rsquo;s rental data is
                  strong, and their audience skews slightly younger. OnTheMarket
                  is worth including for affordability. Ensure your listings
                  include <Link href="/features" className="text-red underline underline-offset-2 hover:text-red/80">video</Link>, as rental properties with video receive
                  significantly more enquiries.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Budget-conscious agent
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  If you can only afford two portals, Rightmove and OnTheMarket
                  offer the best reach-to-cost ratio. Rightmove covers the bulk
                  of searchers, while OnTheMarket provides supplementary
                  exposure at a fraction of Zoopla&rsquo;s cost. However, do not
                  underestimate the vendor leads that Zoopla&rsquo;s valuation
                  tools generate.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Video tip */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <div className="rounded-xl border-2 border-red/20 bg-red/5 p-8 md:p-10">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                Pro tip
              </p>
              <h2 className="mt-4 font-display text-2xl font-bold text-charcoal md:text-3xl">
                Stand out on every portal with video
                <span className="text-red">.</span>
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate">
                All three portals support embedded video, and listings with
                video consistently receive more clicks and enquiries than those
                without. The challenge for most agents is{" "}
                <Link href="/how-it-works" className="text-red underline underline-offset-2 hover:text-red/80">creating video at
                scale</Link>: filming, editing, and formatting for each listing takes
                hours.
              </p>
              <p className="mt-4 text-base leading-relaxed text-slate">
                QuiteRight solves this. Paste a{" "}
                <Link href="/integrations/rightmove" className="text-red underline underline-offset-2 hover:text-red/80">Rightmove</Link>,{" "}
                <Link href="/integrations/zoopla" className="text-red underline underline-offset-2 hover:text-red/80">Zoopla</Link>, or{" "}
                <Link href="/integrations/onthemarket" className="text-red underline underline-offset-2 hover:text-red/80">OnTheMarket</Link> URL and get a professional property video in 60
                seconds. Add it back to your portal listings to increase
                engagement, then publish to{" "}
                <Link href="/guides/social-media-estate-agents" className="text-red underline underline-offset-2 hover:text-red/80">social media</Link> for even wider reach.
              </p>
              <div className="mt-6 flex flex-col gap-4 sm:flex-row">
                <Link
                  href="/sign-up"
                  className="inline-flex items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90"
                >
                  Start your free trial
                </Link>
                <Link
                  href="/how-it-works"
                  className="inline-flex items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone"
                >
                  See how it works
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              FAQ
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <FaqAccordion items={FAQ} />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Related guides */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <h2 className="font-display text-2xl font-bold text-charcoal">
              Related guides
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {[
              {
                href: "/guides/rightmove-listing-optimisation",
                title: "Rightmove listing optimisation",
                desc: "Get more views and enquiries from your Rightmove listings.",
              },
              {
                href: "/guides/instagram-vs-facebook-vs-tiktok-property-ads",
                title: "Instagram vs Facebook vs TikTok",
                desc: "Which social platform is best for advertising your listings?",
              },
              {
                href: "/guides/property-video-marketing",
                title: "Property video marketing",
                desc: "The complete guide to video marketing for estate agents.",
              },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Read guide
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-3.5 transition-transform group-hover:translate-x-0.5"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
              Make every portal listing work harder
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
              Add{" "}
              <Link href="/guides/property-video-marketing" className="text-warm-grey underline underline-offset-2 hover:text-cream">professional video</Link> to your Rightmove, Zoopla, and OnTheMarket
              listings in 60 seconds. More clicks, more viewings, more
              instructions.
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/sign-up"
                className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
              >
                Start your free trial
              </Link>
              <Link
                href="/demo"
                className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
              >
                Book a demo
              </Link>
            </div>
            <p className="mt-5 text-sm text-warm-grey">
              14-day free trial &middot; No credit card &middot; Cancel any time
            </p>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  )
}
